import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Download as DownloadIcon, Refresh as RefreshIcon, Delete as DeleteIcon, Info as InfoIcon } from '@mui/icons-material';
import PageHeader from 'components/PageHeader';
import EmptyStatePlaceholder from 'components/EmptyStatePlaceholder';
import buildRoute from 'components/buildRoute';
import BigQueryDatasetSelector from 'components/bigquery/BigQueryDatasetSelector';
import { getCsrfToken } from 'utils/csrf';
import { trackEvent, trackEngagementActivity } from '../../configureMixpanel';

export const CrosstabsIndexPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [crosstabs, setCrosstabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, crosstab: null });
  const [reuseDialog, setReuseDialog] = useState({ 
    open: false, 
    crosstab: null,
    newName: '',
    selectedDataset: null,
    isLoading: false,
    error: null
  });

  const fetchCrosstabs = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await fetch(`/crosstabs/jobs/?project_id=${projectId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch crosstabs: ${response.status}`);
      }
      
      const data = await response.json();
      
      const crosstabsArray = Array.isArray(data) ? data : [];
      setCrosstabs(crosstabsArray);
    } catch (err) {
      console.error('Error fetching crosstabs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Trigger manual job processing for PENDING/RUNNING jobs
  const triggerJobProcessing = async () => {
    try {
      const response = await fetch('/crosstabs/process-jobs/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRFToken': getCsrfToken() || '',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to trigger job processing: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Manual job processing triggered:', result);
      return result;
    } catch (err) {
      console.error('Error triggering job processing:', err);
      throw err;
    }
  };

  // Enhanced refresh that also processes pending jobs
  const handleManualRefresh = async () => {
    try {
      setIsRefreshing(true);
      
      // First trigger job processing for any PENDING/RUNNING jobs
      await triggerJobProcessing();
      
      // Wait a moment for jobs to potentially complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Then refresh the list to show updated statuses
      await fetchCrosstabs(true);
      
    } catch (err) {
      console.error('Error during manual refresh:', err);
      setError(err.message);
      setIsRefreshing(false);
    }
  };

  const handleCreateNew = () => {
    // Clear any existing session storage data for a fresh start
    sessionStorage.removeItem('crosstabFormData');
    // Navigate to step 1 of the wizard
    navigate(`/projects/${projectId}/crosstabs/new/step1`);
  };

  useEffect(() => {
    fetchCrosstabs();
    trackEngagementActivity("crosstabs_index_viewed");
  }, []);

  // Auto-refresh for processing crosstabs
  useEffect(() => {
    const hasProcessingJobs = crosstabs.some(c => c.state === 'PENDING' || c.state === 'RUNNING');
    
    if (hasProcessingJobs && !refreshInterval) {
      const interval = setInterval(() => {
        fetchCrosstabs();
      }, 10000); // Refresh every 10 seconds
      setRefreshInterval(interval);
    } else if (!hasProcessingJobs && refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [crosstabs, refreshInterval]);

  const getStatusChip = (state) => {
    const statusConfig = {
      'PENDING': { label: 'Processing', color: 'warning' },
      'RUNNING': { label: 'Processing', color: 'warning' },
      'SUCCEEDED': { label: 'Ready', color: 'success' },
      'FAILED': { label: 'Failed', color: 'error' },
    };

    const config = statusConfig[state] || { label: state || 'Unknown', color: 'default' };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getDataSourceName = (crosstab) => {
    // Extract dataset name from the table path (project.dataset.table)
    if (crosstab.survey_responses_table && typeof crosstab.survey_responses_table === 'string') {
      const parts = crosstab.survey_responses_table.split('.');
      if (parts.length === 3) {
        return parts[1]; // Return the dataset name
      }
      // If not in expected format, return the whole thing
      return crosstab.survey_responses_table;
    }
    // Handle null/undefined cases
    return crosstab.survey_responses_table || 'No data source';
  };

  const handleDownload = (crosstab) => {
    // Use signed download URL for secure access, fallback to output_url
    const downloadUrl = crosstab.signed_download_url || crosstab.output_url;
    if (downloadUrl) {
      trackEvent("crosstab_downloaded", {
        url_type: crosstab.signed_download_url ? "signed_download_url" : "output_url",
      });
      window.open(downloadUrl, '_blank');
    }
  };

  const handleReuse = (crosstab) => {
    // Open the reuse dialog with the crosstab data
    setReuseDialog({
      open: true,
      crosstab: crosstab,
      newName: '',
      selectedDataset: null,
      isLoading: false,
      error: null
    });
  };

  const handleCloseReuseDialog = () => {
    setReuseDialog({
      open: false,
      crosstab: null,
      newName: '',
      selectedDataset: null,
      isLoading: false,
      error: null
    });
  };

  const handleCreateReusedCrosstab = async () => {
    if (!reuseDialog.newName.trim() || !reuseDialog.selectedDataset) {
      setReuseDialog(prev => ({
        ...prev,
        error: 'Please fill in all required fields'
      }));
      return;
    }

    setReuseDialog(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Fetch the original crosstab configuration
      const response = await fetch(`/crosstabs/jobs/${reuseDialog.crosstab.id}/reuse_config/`);
      if (!response.ok) {
        throw new Error('Failed to load crosstab configuration');
      }
      
      const data = await response.json();
      
      // Build the submission payload using the original config with new name and dataset
      const originalConfig = data.config;
      
      // Build full table names with the new dataset
      const fullSurveyResponsesTable = `${projectId}.${reuseDialog.selectedDataset}.${originalConfig.surveyResponseTable}`;
      const fullCnlTable = `${projectId}.${reuseDialog.selectedDataset}.${originalConfig.questionLabelsTable}`;
      const fullVvlTable = `${projectId}.${reuseDialog.selectedDataset}.${originalConfig.responseLabelsTable}`;
      
      // Generate output filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const outputFilename = `crosstab_${timestamp}.xlsx`;
      
      // Extract columns and rows from the original configuration
      const userConfigurations = originalConfig.userConfigurations || {};
      const columns = userConfigurations.columns || [];
      const rows = userConfigurations.rows || userConfigurations.columns || []; // Fallback to columns if rows not available
      
      // Validate required fields
      if (!columns.length) {
        throw new Error('No column configurations found in original crosstab');
      }
      if (!rows.length) {
        throw new Error('No row configurations found in original crosstab');
      }

      // Build the complete submission payload
      const submissionPayload = {
        name: reuseDialog.newName,
        survey_responses_table: fullSurveyResponsesTable,
        cnl_table: fullCnlTable,
        vvl_table: fullVvlTable,
        columns: columns,
        rows: rows,
        output_filename: outputFilename,
        weight_key: originalConfig.weightKeyField || null,
        value_collapses: userConfigurations.value_collapses || {},
        original_config: {
          ...originalConfig,
          crosstabName: reuseDialog.newName,
          selectedDataset: reuseDialog.selectedDataset,
          isReuse: true,
          originalCrosstabId: reuseDialog.crosstab.id,
          originalName: data.original_name
        }
      };

      // Submit the crosstab directly
      const csrfToken = getCsrfToken();
      const submitResponse = await fetch(`/crosstabs/submit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify(submissionPayload),
      });

      const submitResult = await submitResponse.json();

      if (!submitResponse.ok) {
        console.error('Submission failed. Response:', submitResult);
        console.error('Payload sent:', submissionPayload);
        let errorMessage = submitResult.error || `Failed to submit crosstab (${submitResponse.status})`;
        if (submitResult.details) {
          console.error('Validation errors:', submitResult.details);
          errorMessage += '. Check console for validation details.';
        }
        throw new Error(errorMessage);
      }

      if (!submitResult.success || !submitResult.job_id) {
        throw new Error(submitResult.error || 'Crosstab submission failed');
      }

      trackEvent("crosstab_submitted", { source: "reuse_dialog", success: true });

      // Success! Close dialog and refresh the list to show the new job
      handleCloseReuseDialog();
      await fetchCrosstabs(true); // Refresh the crosstabs list

    } catch (error) {
      console.error('Error creating reused crosstab:', error);
      trackEvent("crosstab_submitted", { source: "reuse_dialog", success: false });
      setReuseDialog(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to create crosstab'
      }));
    }
  };

  const handleEditCrosstab = async () => {
    if (!reuseDialog.newName.trim() || !reuseDialog.selectedDataset) {
      setReuseDialog(prev => ({
        ...prev,
        error: 'Please fill in all required fields'
      }));
      return;
    }

    try {
      // Get the original crosstab configuration
      const response = await fetch(`/crosstabs/jobs/${reuseDialog.crosstab.id}/reuse_config/`);
      if (!response.ok) {
        throw new Error('Failed to load crosstab configuration');
      }
      
      const data = await response.json();
      const originalConfig = data.config;
      
      // Map the original config to the wizard's expected format
      const editConfig = {
        // Step 1 data - use NEW name and dataset from popup, original table names
        crosstabName: reuseDialog.newName,
        selectedDataset: reuseDialog.selectedDataset,
        surveyResponseTable: originalConfig.surveyResponseTable,
        questionLabelsTable: originalConfig.questionLabelsTable,
        responseLabelsTable: originalConfig.responseLabelsTable,
        
        // Step 2 data - use original field mappings
        questionIdField: originalConfig.questionIdField || 'q_id',
        questionTextField: originalConfig.questionTextField || 'q_text',
        questionTypeField: originalConfig.questionTypeField || 'q_type',
        parentQuestionIdField: originalConfig.parentQuestionIdField || 'p_id',
        optionTextField: originalConfig.optionTextField || 'a_text',
        responseField: originalConfig.responseField || 'a_id',
        weightKeyField: originalConfig.weightKeyField || '',
        
        // Step 3 & 4 data - separate row and column questions
        // Use original selections (which include parent questions) if available,
        // otherwise fall back to the formatted rows/columns (which only have children)
        selectedRowQuestions: originalConfig.userConfigurations?.original_row_selections || 
                            originalConfig.userConfigurations?.rows?.map(row => row.column_name) || [],
        selectedColumnQuestions: originalConfig.userConfigurations?.original_column_selections || 
                               originalConfig.userConfigurations?.columns?.map(col => col.column_name) || [],
        selectedQuestions: [
          ...(originalConfig.userConfigurations?.original_row_selections || 
              originalConfig.userConfigurations?.rows?.map(row => row.column_name) || []),
          ...(originalConfig.userConfigurations?.original_column_selections || 
              originalConfig.userConfigurations?.columns?.map(col => col.column_name) || [])
        ],
        
        // Step 5 data - preserve all original configurations
        // Use raw state (with state keys) if available, otherwise fall back to formatted data
        responseOrders: originalConfig.userConfigurations?.response_orders || {},
        hiddenResponses: originalConfig.userConfigurations?.hidden_responses || {},
        collapses: originalConfig.userConfigurations?.value_collapses_raw || {},
        
        // Preserve the full userConfigurations for the wizard
        userConfigurations: originalConfig.userConfigurations || {},
        
        // Edit metadata
        isEdit: true,
        originalCrosstabId: reuseDialog.crosstab.id,
        originalName: data.original_name
      };

      // Store the configuration in session storage for the wizard
      sessionStorage.setItem('crosstabFormData', JSON.stringify(editConfig));
      
      // Close the dialog and navigate to step 1 of the wizard
      handleCloseReuseDialog();
      navigate(`/projects/${projectId}/crosstabs/new/step1`);
      
    } catch (error) {
      console.error('Error preparing crosstab for editing:', error);
      setReuseDialog(prev => ({
        ...prev,
        error: error.message || 'Failed to prepare crosstab for editing'
      }));
    }
  };

  const handleDelete = async (crosstab) => {
    if (!window.confirm(`Are you sure you want to delete "${crosstab.name}"?`)) {
      return;
    }
    
    try {
      // Get CSRF token for the DELETE request
      const getCsrfToken = () => {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'csrftoken') {
            return value;
          }
        }
        return null;
      };

      const response = await fetch(`/crosstabs/jobs/${crosstab.id}/`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': getCsrfToken(),
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete crosstab');
      }

      trackEvent("crosstab_deleted", {});

      // Clear any existing error and refresh the list after deletion
      setError(null);
      await fetchCrosstabs();

    } catch (error) {
      console.error('Error deleting crosstab:', error);
      setError('Failed to delete crosstab');
    }
  };

  const handleShowDetails = (crosstab) => {
    setDetailsDialog({ open: true, crosstab });
  };

  const handleCloseDetails = () => {
    setDetailsDialog({ open: false, crosstab: null });
  };

  const getQuestionCount = (crosstab) => {
    if (crosstab.original_config?.userConfigurations?.columns) {
      return crosstab.original_config.userConfigurations.columns.length;
    }
    return 'N/A';
  };

  const getUniqueQuestionIds = (crosstab) => {
    const questionIds = new Set();
    
    // Extract from columns array
    if (crosstab.columns) {
      crosstab.columns.forEach(col => {
        if (col.column_name) {
          questionIds.add(col.column_name);
        }
      });
    }
    
    // Extract from rows array
    if (crosstab.rows) {
      crosstab.rows.forEach(row => {
        if (row.column_name) {
          questionIds.add(row.column_name);
        }
      });
    }
    
    return Array.from(questionIds).sort();
  };


  const CreateCrosstabButton = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <IconButton
        onClick={handleManualRefresh}
        disabled={isRefreshing}
        title="Refresh"
        size="small"
      >
        <RefreshIcon />
      </IconButton>
      <Button
        onClick={handleCreateNew}
        variant="contained"
        color="secondary"
        aria-label="Create New Crosstab"
      >
        Create new
      </Button>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ pt: 4 }}>
        <PageHeader
          title="Crosstabs"
          subtitle={`Project: ${projectId}`}
          action={<CreateCrosstabButton />}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading crosstabs...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: 4 }}>
        <PageHeader
          title="Crosstabs"
          subtitle={`Project: ${projectId}`}
          action={<CreateCrosstabButton />}
        />
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
            >
              <RefreshIcon />
            </IconButton>
          }
        >
          Error loading crosstabs: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 4 }}>
      <PageHeader
        title="Crosstabs"
        subtitle={`Project: ${projectId}`}
        action={<CreateCrosstabButton />}
      />
      
      <Box component="section" sx={{ mt: 3, position: 'relative' }}>
        {isRefreshing && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'background.paper',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Refreshing...
            </Typography>
          </Box>
        )}
        {crosstabs.length === 0 ? (
          <EmptyStatePlaceholder
            title="No crosstabs to show"
            subtitle="Once you create a crosstab, it will show up here."
          />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>PAD Data Source</strong></TableCell>
                  <TableCell><strong>Date Created</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {crosstabs.map((crosstab) => (
                  <TableRow key={crosstab.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {crosstab.name || `Crosstab ${crosstab.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getDataSourceName(crosstab)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(crosstab.created)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(crosstab.state)}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {crosstab.state === 'SUCCEEDED' && (crosstab.signed_download_url || crosstab.output_url) && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleDownload(crosstab)}
                            sx={{ textDecoration: 'underline', minWidth: 'auto' }}
                          >
                            Download
                          </Button>
                        )}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleReuse(crosstab)}
                          sx={{ textDecoration: 'underline', minWidth: 'auto' }}
                        >
                          Reuse
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleShowDetails(crosstab)}
                          sx={{ color: 'primary.main' }}
                        >
                          <InfoIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(crosstab)}
                          size="small"
                          color="error"
                          title="Delete Crosstab"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Crosstab Details
        </DialogTitle>
        <DialogContent>
          {detailsDialog.crosstab && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h4" gutterBottom>
                {detailsDialog.crosstab.name || `Crosstab ${detailsDialog.crosstab.id}`}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <List dense>
                  {detailsDialog.crosstab.original_config && (
                    <>
                      <ListItem>
                        <ListItemText 
                          primary="Dataset" 
                          secondary={detailsDialog.crosstab.original_config.selectedDataset || 'N/A'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Survey Responses Table" 
                          secondary={detailsDialog.crosstab.original_config.surveyResponseTable || 'N/A'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Question Labels Table" 
                          secondary={detailsDialog.crosstab.original_config.questionLabelsTable || 'N/A'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Response Labels Table" 
                          secondary={detailsDialog.crosstab.original_config.responseLabelsTable || 'N/A'} 
                        />
                      </ListItem>
                    </>
                  )}
                  <ListItem>
                    <ListItemText 
                      primary="Questions Selected" 
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {(() => {
                            const questionIds = getUniqueQuestionIds(detailsDialog.crosstab);
                            if (questionIds.length === 0) {
                              return <Typography variant="body2" color="text.secondary">No questions available</Typography>;
                            }
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {questionIds.map((questionId) => (
                                  <Chip
                                    key={questionId}
                                    label={questionId}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            );
                          })()}
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reuse Crosstab Dialog */}
      <Dialog
        open={reuseDialog.open}
        onClose={handleCloseReuseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reuse Crosstab
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Create a new crosstab by reusing the configuration from "{reuseDialog.crosstab?.name || `Crosstab ${reuseDialog.crosstab?.id}`}".
              Enter a new name and select a new survey data file.
            </Typography>

            {/* Error Alert */}
            {reuseDialog.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {reuseDialog.error}
              </Alert>
            )}

            {/* New Crosstab Name */}
            <TextField
              fullWidth
              label="New crosstab name"
              value={reuseDialog.newName}
              onChange={(e) => setReuseDialog(prev => ({ ...prev, newName: e.target.value, error: null }))}
              error={!reuseDialog.newName.trim() && reuseDialog.error}
              sx={{ mb: 3 }}
            />

            <Typography variant="body1" sx={{ mb: 2 }}>
              Select New Survey Data file
            </Typography>

            {/* Dataset Selector */}
            <BigQueryDatasetSelector
              selectedDataset={reuseDialog.selectedDataset}
              onSelectDataset={(event, value) => {
                setReuseDialog(prev => ({
                  ...prev,
                  selectedDataset: value,
                  error: null
                }));
              }}
              showError={!reuseDialog.selectedDataset && reuseDialog.error}
              label="Select dataset"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseReuseDialog}
            disabled={reuseDialog.isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditCrosstab}
            variant="outlined"
            disabled={reuseDialog.isLoading}
            sx={{ minWidth: 120 }}
          >
            Edit Crosstab
          </Button>
          <Button 
            onClick={handleCreateReusedCrosstab}
            variant="contained"
            disabled={reuseDialog.isLoading}
            sx={{ minWidth: 140 }}
          >
            {reuseDialog.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Crosstab'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const routeArgs = {
  element: <CrosstabsIndexPage />,
};

export default buildRoute(routeArgs);